/* ============================================================
   ÉPRIS Loyalty UI — Core
   ============================================================
   Liest window.loyaltylion.customer (LoyaltyLion JS-SDK) und
   exposed eine zentrale state + event-API für alle UI-Komponenten
   (Header-Badge, Mini-Panel, PDP-Section, Cart-Drawer-Line).

   Single source of truth: window.eprisLoyalty
     .state = {
       isLoggedIn: boolean,
       points: number,           // approved + pending — was Customer "fühlt"
       pointsApproved: number,
       pointsPending: number,
       tierName: 'Silver' | 'Gold' | 'Platinum' | 'Privé' | null,
       tierIndex: 0..3 | null,   // 0=Silver
       nextTier: { name, threshold, ptsToGo } | null,
       nextReward: { name, threshold, ptsToGo, valueText } | null
     }
     .onUpdate(fn)               → unsubscribe
     .openMiniPanel()            → triggert UI-event 'epris:loyalty:open-panel'

   Events on document:
     'epris:loyalty:update'      detail: {state}
     'epris:loyalty:open-panel'  (von Header-Badge → Mini-Panel)
   ============================================================ */

(function() {
  'use strict';

  /* Tier-Definitionen mit LoyaltyLion-IDs (Stand 2026-05-02, Site 58453).
     IDs verifiziert via Admin-URL https://app.loyaltylion.com/sites/58453/manage/tiers/{id}.
     Bei Änderung im LL-Admin: hier id+threshold updaten + Klaviyo-Trigger sync halten. */
  const TIERS = [
    { id: 28400, name: 'Silver',   threshold: 0,    headlinePerk: 'Free Shipping ab 150 Pts',  benefits: ['Free Shipping (ab 150 Pts)'] },
    { id: 28401, name: 'Gold',     threshold: 1000, headlinePerk: 'Free Shipping inklusive',   benefits: ['Free Shipping', 'Early Access', 'Geburtstagsgeschenk'] },
    { id: 28402, name: 'Platinum', threshold: 2500, headlinePerk: 'Permanent −10% Rabatt',     benefits: ['Permanent −10% Rabatt', 'Free Shipping', 'Early Access', 'Geburtstagsgeschenk'] },
    { id: 28403, name: 'Privé',    threshold: 4500, headlinePerk: 'Permanent −15% Rabatt',     benefits: ['Permanent −15% Rabatt', 'Priority Shipping', 'Free Shipping', 'Early Access', 'Geburtstagsgeschenk'] }
  ];

  /* Reward-Schwellen aus LoyaltyLion-Admin */
  const REWARDS = [
    { threshold: 150,  name: 'Free Shipping',         valueText: 'Free Shipping' },
    { threshold: 500,  name: '−€5 Rabatt',            valueText: '−€5 Rabatt' },
    { threshold: 500,  name: 'Nachfüllbarer Zerstäuber', valueText: 'Nachfüllbarer Zerstäuber' },
    { threshold: 1000, name: '−€10 Rabatt',           valueText: '−€10 Rabatt' },
    { threshold: 1500, name: '−€15 Rabatt',           valueText: '−€15 Rabatt' },
    { threshold: 2500, name: 'Mystery Duft 50ml',     valueText: 'Mystery Duft 50ml' }
  ];

  const POINTS_PER_EURO = 5;   /* Earn-Rate */
  const EURO_PER_POINT = 0.01; /* Redemption: 100 Pts = €1 */

  const listeners = new Set();
  let state = createInitialState();

  function createInitialState() {
    return {
      isLoggedIn: false,
      points: 0,
      pointsApproved: 0,
      pointsPending: 0,
      pointsRedeemable: 0,
      pointsSpent: 0,
      pointsLifetime: 0,
      pointsTotal: 0,
      rewardsClaimedCount: 0,
      tierName: null,
      tierIndex: null,
      tierMembership: null,
      nextTier: null,
      nextReward: null,
      pointsValueEuros: 0,
      firstName: null,
      referralLink: null
    };
  }

  function computeDerived(s) {
    /* Tier-Index: wenn LoyaltyLion einen tier.name liefert, hat der Vorrang vor Pts-Rechnung
       (Customer kann via Admin in höherem Tier sitzen als seine pointsApproved hergäben).
       Pts-basierte Logik nur als Fallback wenn LL kein tier liefert. */
    let tierIdx = -1;
    if (s.tierName) {
      const normalized = s.tierName.toLowerCase().replace(/[^a-z]/g, '');
      tierIdx = TIERS.findIndex(t => t.name.toLowerCase().replace(/[^a-z]/g, '') === normalized);
    }
    if (tierIdx === -1) {
      tierIdx = 0;
      for (let i = TIERS.length - 1; i >= 0; i--) {
        if (s.pointsApproved >= TIERS[i].threshold) { tierIdx = i; break; }
      }
      if (!s.tierName) s.tierName = TIERS[tierIdx].name;
    }
    s.tierIndex = tierIdx;

    /* Next-Tier */
    const next = TIERS[tierIdx + 1];
    s.nextTier = next
      ? { name: next.name, threshold: next.threshold, ptsToGo: Math.max(0, next.threshold - s.pointsApproved), benefits: next.benefits }
      : null;

    /* Next-Reward — der nächste den Customer NOCH NICHT erreicht hat */
    const usable = s.points; /* approved + pending — Reward-Verfügbarkeit checkt LL selber */
    const nextR = REWARDS.find(r => r.threshold > usable);
    s.nextReward = nextR
      ? { name: nextR.name, threshold: nextR.threshold, ptsToGo: nextR.threshold - usable, valueText: nextR.valueText }
      : null;

    s.pointsValueEuros = (usable * EURO_PER_POINT);
    return s;
  }

  function pointsForCart(cartTotalCents) {
    /* Wieviele Pts mit dieser Bestellung erworben werden — based on cart total */
    return Math.floor((cartTotalCents / 100) * POINTS_PER_EURO);
  }

  function pointsForPrice(priceCents) {
    return Math.floor((priceCents / 100) * POINTS_PER_EURO);
  }

  function notify() {
    listeners.forEach(fn => {
      try { fn(state); } catch (e) { console.warn('[loyalty listener]', e); }
    });
    document.dispatchEvent(new CustomEvent('epris:loyalty:update', { detail: { state } }));
  }

  function tierByLLId(id) {
    if (id == null) return null;
    return TIERS.find(t => t.id === Number(id)) || null;
  }

  function readTierName(c) {
    /* Primärer Pfad bei LoyaltyLion: c.loyaltyTierMembership.loyaltyTierId — ID-basiertes Lookup */
    if (!c) return null;
    const ltm = c.loyaltyTierMembership;
    if (ltm && ltm.loyaltyTierId != null) {
      const matched = tierByLLId(ltm.loyaltyTierId);
      if (matched) return matched.name;
    }
    /* Fallback-Pfade falls SDK-Schema sich ändert oder andere Stores das anders exposeen */
    if (c.tier && typeof c.tier === 'object' && c.tier.name) return String(c.tier.name);
    if (typeof c.tier === 'string' && c.tier) return c.tier;
    if (c.tierName) return String(c.tierName);
    if (ltm && typeof ltm === 'object') {
      if (ltm.tierName) return String(ltm.tierName);
      if (ltm.name) return String(ltm.name);
      if (ltm.tier && typeof ltm.tier === 'object' && ltm.tier.name) return String(ltm.tier.name);
      if (ltm.tier && typeof ltm.tier === 'string') return ltm.tier;
      if (ltm.loyaltyTierName) return String(ltm.loyaltyTierName);
    }
    if (c.currentTierName) return String(c.currentTierName);
    if (c.currentTier && c.currentTier.name) return String(c.currentTier.name);
    return null;
  }

  function syncFromLoyaltyLion() {
    const ll = window.loyaltylion;
    if (!ll || !ll.customer) return;
    const c = ll.customer;
    state.isLoggedIn = !!c.isLoggedIn;
    /* Pts: alle Felder mappen ohne Daten-Verlust. UI entscheidet was angezeigt wird. */
    state.pointsApproved = Number(c.pointsApproved) || 0;
    state.pointsPending = Number(c.pointsPending) || 0;
    state.pointsRedeemable = Number(c.pointsRedeemable) || 0;
    state.pointsSpent = Number(c.pointsSpent) || 0;
    state.pointsLifetime = Number(c.pointsLifetime) || 0;
    state.pointsTotal = Number(c.pointsTotal) || 0;
    /* state.points = "what customer feels they have" — bevorzugt redeemable, fallback approved */
    state.points = state.pointsRedeemable > 0 ? state.pointsRedeemable
      : (state.pointsApproved + state.pointsPending) > 0 ? (state.pointsApproved + state.pointsPending)
      : 0;
    /* claimedRewards count — für History-Tab */
    state.rewardsClaimedCount = Array.isArray(c.claimedRewards) ? c.claimedRewards.length : (Number(c.rewardsClaimedCount) || 0);
    /* Tier-Membership */
    state.tierName = readTierName(c);
    state.tierMembership = c.loyaltyTierMembership || null;
    /* Customer-Identität */
    state.firstName = c.firstName || null;
    state.referralLink = c.referralUrl || c.referralLink || null;
    /* Debug-Snapshot — full content für Inspection */
    window.eprisLoyaltyDebug = {
      raw: {
        loyaltyTierMembership: c.loyaltyTierMembership,
        pointsApproved: c.pointsApproved,
        pointsPending: c.pointsPending,
        pointsRedeemable: c.pointsRedeemable,
        pointsSpent: c.pointsSpent,
        pointsLifetime: c.pointsLifetime,
        pointsTotal: c.pointsTotal,
        claimedRewards: c.claimedRewards
      },
      resolved: {
        tierName: state.tierName,
        points: state.points,
        pointsApproved: state.pointsApproved,
        pointsRedeemable: state.pointsRedeemable,
        pointsLifetime: state.pointsLifetime,
        rewardsClaimedCount: state.rewardsClaimedCount
      }
    };
    computeDerived(state);
    notify();
  }

  function init() {
    const ll = window.loyaltylion;
    if (!ll) {
      /* SDK nicht geladen → retry für 10s */
      if ((init._retries = (init._retries || 0) + 1) > 50) return;
      return setTimeout(init, 200);
    }
    /* SDK loaded — wait for bootstrap */
    if (typeof ll.on === 'function') {
      ll.on('ready', syncFromLoyaltyLion);
      ll.on('customer:loaded', syncFromLoyaltyLion);
      ll.on('customer:updated', syncFromLoyaltyLion);
    }
    /* Initial sync (falls customer schon ready) */
    syncFromLoyaltyLion();
    /* Plus periodic poll als safety-net (LL hat manchmal silent updates) */
    setInterval(syncFromLoyaltyLion, 5000);
  }

  function getReferralLink() {
    const ll = window.loyaltylion;
    if (ll && ll.customer) {
      if (ll.customer.referralLink) return ll.customer.referralLink;
      if (ll.customer.referralUrl) return ll.customer.referralUrl;
      if (ll.customer.id) {
        return location.origin + '/pages/epris-loyalty-programm?ref=' + encodeURIComponent(ll.customer.id);
      }
    }
    return location.origin + '/pages/epris-loyalty-programm';
  }

  /* Public API */
  window.eprisLoyalty = {
    get state() { return state; },
    onUpdate(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    openMiniPanel() { document.dispatchEvent(new CustomEvent('epris:loyalty:open-panel')); },
    openInfoModal() { document.dispatchEvent(new CustomEvent('epris:loyalty:open-info')); },
    pointsForPrice,
    pointsForCart,
    getReferralLink,
    /* Constants (für UI-Komponenten) */
    TIERS, REWARDS,
    POINTS_PER_EURO, EURO_PER_POINT,
    formatPts(n) { return Math.round(n).toLocaleString('de-DE') + ' PTS'; },
    formatEuro(eur) { return '€' + eur.toFixed(2).replace('.', ','); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
